<template>
  <div class="report-groups">
    <panel>
      <div slot="toolbar" class="w-100 p-2 d-flex justify-content-between">
        <w-select :model="$schema.type" :values="reportTypeOptions" @input="selectReportType" />

        <div>
          <w-button
            v-if="!hideGeneralReport"
            class="btn-light mr-2 mt-4"
            text="Wybierz grupy"
            @click="openColumnsModal"
          />

          <w-button class="btn-light mr-2 mt-4" text="Wybierz filtry" @click="openFiltersModal" />
        </div>
      </div>

      <div class="table-responsive">
        <table class="table table-bordered table-hover table-striped w-100 dataTable dtr-inline">
          <thead>
            <tr>
              <th class="w-0">Lp.</th>

              <th class="pointer" title="Sortuj" @click="setOrder(countColumn)" @keydown="setOrder(countColumn)">
                {{ countColumn.title }}
                <i
                  v-if="countColumn.name === sort.order"
                  class="fal fa-fw"
                  :class="{ 'fa-arrow-down': sort.desc, 'fa-arrow-up': !sort.desc }"
                />
              </th>

              <th
                v-for="(column, columnIndex) of allColumns"
                :key="'header_' + columnIndex"
                :style="{ cursor: column.sortable ? 'pointer' : 'default' }"
                :title="column.sortable ? 'Sortuj' : ''"
                @click="setOrder(column)"
                @keydown="setOrder(column)"
              >
                {{ column.title }}
                <i
                  v-if="column.sortBy ? column.sortBy === sort.order : column.name === sort.order"
                  class="fal fa-fw"
                  :class="{ 'fa-arrow-down': sort.desc, 'fa-arrow-up': !sort.desc }"
                />
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(row, rowIndex) of rows" :key="'row_' + rowIndex">
              <td class="text-center">{{ rowIndex + 1 }}</td>

              <td class="text-center">{{ row.count }}</td>

              <td
                v-for="(column, columnIndex) of allColumns"
                :key="'row_' + rowIndex + 'cell_' + columnIndex"
                :class="textPositionClass(column, row)"
              >
                {{ cellText(column, row) }}
              </td>
            </tr>
          </tbody>

          <tfoot>
            <tr>
              <td><b>Razem:</b></td>

              <td class="text-center">{{ totalCount }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div slot="footer" class="float-right">
        <div>
          <post-form
            text="Pobierz CSV"
            class="btn-light mr-2"
            icon="fas fa-download mr-2"
            :url="csvUrl"
            :fields="statisticsFields"
            :disabled="!rows"
          />

          <post-form
            text="Pobierz PDF"
            class="btn-light"
            icon="fas fa-download mr-2"
            :url="pdfUrl"
            :fields="statisticsFields"
            :disabled="!rows"
          />
        </div>
      </div>
    </panel>

    <checkbox-modal
      title="Wybierz grupy"
      class="modal-big"
      checkbox-class="col-12 col-md-6 col-lg-4"
      :options="modalOptions"
      :selected-options="selectedColumnNames"
      :show="showColumnsModal"
      @close="closeColumnsModal"
      @save="saveColumns"
    />

    <filters-modal
      title="Wybierz filtry"
      class="modal-big"
      :filters="filters"
      :show="showFiltersModal"
      @close="closeFiltersModal"
      @save="saveFilters"
    />
  </div>
</template>

<script>
import { tryParseBoolean } from '@witkac/utils/tryParseBoolean';
import moment from 'moment';
import { mapState } from 'vuex';

import CheckboxModal from '@/components/modals/CheckboxModal';
import Panel from '@/components/panel/Panel';
import PostForm from '@/components/postForm/PostForm';
import { Errors } from '@/enums/errors.enum';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { Storage } from '@/enums/storage.enum';
import { date } from '@/filters/date';
import { formMixin } from '@/mixins/form';
import { notificationsMixin } from '@/mixins/notifications';
import { reportFiltersMixin } from '@/mixins/reportFilters';
import { reportTypeSchema } from '@/schemas/reportType.schema';
import { config } from '@/utils/config';
import { parseJsonOrDefault } from '@/utils/parseJsonOrDefault';
import { getReportGroups, getReportGroupsParams } from '@/utils/report';
import { getSelectedReportType, getReportTypes } from '@/utils/reportTypes';

// @vuese
// Pogrupowane statystyki
// @group Reports
export default {
  components: { CheckboxModal, Panel, PostForm },
  mixins: [formMixin, notificationsMixin, reportFiltersMixin()],
  schema: reportTypeSchema,
  data() {
    const selectedReportType = getSelectedReportType(this.$route);
    const availableFilters = JSON.parse(this.$store.state.setting.settings[PublicSettings.REPORT_FILTERS].value);

    return {
      csvUrl: config.csvURL + '/report',
      filters: this.getFilters(selectedReportType?.filters || availableFilters),
      model: { type: selectedReportType?.id },
      pdfUrl: config.renderURL + '/pdf/statistics',
      selectedColumns: this.getColumns(),
      showColumnsModal: false,
      sort: this.$route.query ? { order: this.$route.query.order, desc: this.$route.query.desc } : undefined
    };
  },
  computed: {
    ...mapState({
      rows: state => state.documentIndex.statistics.result,

      reportTypes: state => getReportTypes(state),

      hideGeneralReport: state =>
        tryParseBoolean(state.setting.settings[PublicSettings.HIDE_GENERAL_REPORT]?.value, false)
    }),

    totalCount() {
      return this.rows.map(row => row.count).reduce((total, current) => total + current, 0);
    },

    selectedReportType() {
      if (this.$route.query.type) {
        return this.reportTypes.find(type => type.id === this.$route.query.type);
      }
      if (this.hideGeneralReport) {
        return this.reportTypes[0];
      }
    },

    columns() {
      const columns = this.selectedReportType
        ? this.selectedReportType.groups
        : JSON.parse(this.$store.state.setting.settings.REPORT_GROUPS.value);
      columns.forEach(column => (column.resultName = column.name.replace(/\./g, '__')));
      return columns;
    },

    staticColumns() {
      return (
        this.selectedReportType?.staticGroups?.map(group => ({
          ...group,
          resultName: group.name.replace(/\./g, '__')
        })) || []
      );
    },

    allColumns() {
      return [...this.selectedColumns, ...this.staticColumns];
    },

    countColumn() {
      const column = {
        name: 'count',
        title: 'Liczba złożonych wniosków',
        sortable: true
      };
      if (!this.selectedReportType) {
        return column;
      }

      column.title = this.selectedReportType.countColumn;
      if (this.filters?.[0]?.value?.$gte || this.filters?.[0]?.value?.$lte) {
        const startDate = this.filters[0].value.$gte && date(this.filters[0].value.$gte);
        const endDate =
          this.filters[0].value.$lte &&
          date(
            moment(this.filters[0].value.$lte)
              .subtract(1, 'day')
              .toDate()
          );
        if (startDate && endDate) {
          column.title += ` w okresie od ${startDate} do ${endDate}`;
        } else if (startDate) {
          column.title += ` w okresie od ${startDate}`;
        } else if (endDate) {
          column.title += ` w okresie do ${endDate}`;
        }
      }
      return column;
    },

    reportTypeOptions() {
      const reportTypes = this.reportTypes.map(type => ({ value: type.id, text: type.title }));
      if (this.hideGeneralReport) {
        return reportTypes;
      }
      const defaultType = { value: null, text: 'Ogólny' };
      return [defaultType, ...reportTypes];
    },

    modalOptions() {
      return this.columns.map(column => ({ text: column.title, value: column.name }));
    },

    selectedColumnNames() {
      return this.selectedColumns.map(column => column.name);
    },

    statisticsFields() {
      this.filters; // pola nie odświeżą się po zmianie filtrów bez tego

      const enums = this.selectedColumns
        .filter(column => !!column.enum)
        .reduce((acc, column) => {
          acc[column.resultName] = column.enum;
          return acc;
        }, {});

      const headersMap = [...this.selectedColumns, ...this.staticColumns].reduce((acc, column) => {
        acc[column.resultName] = column.title;
        return acc;
      }, {});
      headersMap.count = this.countColumn.title;

      const params = getReportGroupsParams(this.$route);

      return [
        ...Object.keys(params).map(name => ({ name, value: JSON.stringify(params[name]) })),
        { name: 'headersMap', value: JSON.stringify(headersMap) },
        { name: 'enums', value: JSON.stringify(enums) }
      ];
    }
  },

  methods: {
    // @vuese
    // Ustawia typ raportu
    selectReportType(type) {
      sessionStorage.removeItem(Storage.REPORT_FILTERS + this.$route.params.procedureId.toString());

      const availableFilters = JSON.parse(this.$store.state.setting.settings[PublicSettings.REPORT_FILTERS].value);
      const selectedReportType = type && this.reportTypes.find(reportType => reportType.id === type);
      this.filters = this.getFilters(selectedReportType?.filters || availableFilters);

      this.saveColumns([], { preventUpdate: true });
      this.$router.replace({ query: { ...this.$route.query, type } });
    },

    // @vuese
    // Pobiera kolumny ze storage
    getColumns() {
      const storageColumns = sessionStorage.getItem(Storage.REPORT_GROUPS + this.$route.params.procedureId);
      if (storageColumns) {
        const columns = parseJsonOrDefault(storageColumns);
        if (columns) {
          return columns;
        }
        sessionStorage.removeItem(Storage.REPORT_GROUPS + this.$route.params.procedureId);
      }
      return [];
    },

    // @vuese
    // Klasa przycisku do wyboru kolumny
    columnButtonClass(column) {
      if (this.selectedColumns.some(selectedColumn => selectedColumn.name === column.name)) {
        return 'btn-primary';
      }
      return 'btn-light';
    },

    // @vuese
    // Pobiera raport
    downloadGroups() {
      this.notifySuccess('Pobrano raporty');
    },

    // @vuese
    // Zwraca klasę ustawiającą pozycję tekstu w zależności od typu danych
    textPositionClass(column, row) {
      const value = this.cellText(column, row);
      if (typeof value === 'number') {
        return 'text-right';
      }
      if (typeof value === 'boolean') {
        return 'text-center';
      }
      return 'text-left';
    },

    // @vuese
    // Treść komórki
    cellText(column, row) {
      const value = this.rowValue(column, row);
      if (column.enum) {
        return column.enum[value];
      }
      if (value == null || value === '') {
        return '';
      }
      return value;
    },

    // @vuese
    // Zwraca wartość wiersza dla danej kolumny
    rowValue(column, row) {
      const value = row._id[column.resultName];
      if (column.nameTransformer) {
        if (typeof column.nameTransformer === 'string') {
          return new Function('value', column.nameTransformer)(value);
        }
        return column.nameTransformer(value);
      }
      return value;
    },

    // @vuese
    // Aktualizuje listę
    updateList() {
      getReportGroups(this.$route);
    },

    // @vuese
    // Otwiera modal z wyborem kolumn
    openColumnsModal() {
      this.showColumnsModal = true;
    },

    // @vuese
    // Zamyka modal z wyborem kolumn
    closeColumnsModal() {
      this.showColumnsModal = false;
    },

    // @vuese
    // Zapisuje wybrane kolumny
    saveColumns(columns, { preventUpdate } = {}) {
      this.selectedColumns = this.columns.filter(column => columns.includes(column.name));
      sessionStorage.setItem(Storage.REPORT_GROUPS + this.procedureId, JSON.stringify(this.selectedColumns));
      if (!preventUpdate) {
        this.updateList();
      }
      this.closeColumnsModal();
    },

    // @vuese
    // Ustawianie sortowania tabeli
    setOrder(column) {
      if (column.sortable) {
        const desc = this.sort.order === (column.sortBy ?? column.name) ? !this.sort.desc : true;
        this.order({ order: column.sortBy ?? column.name, desc: typeof desc === 'boolean' ? desc : true });
      }
    },

    // @vuese
    // Zmiana sortowania tabeli
    order(order) {
      this.sort = order;
      this.reload({ ...this.$route.query, ...order });
    },

    // @vuese
    // Przeładowuje stronę
    reload(query) {
      const nextRoute = { name: this.$route.name, params: this.$route.params, query };
      this.$router.replace(nextRoute);
    }
  },
  beforeRouteUpdate(to, from, next) {
    getReportGroups(to)
      .then(() => next())
      .catch(err => this.notifyError(Errors.REPORT_CANNOT_GET));
  }
};
</script>

<style>
.columns .columns-title {
  font-weight: 600;
}

.pointer {
  cursor: pointer;
}

.report-groups .panel .panel-toolbar {
  width: 100%;
}

th.w-0 {
  width: 0;
}
</style>
